import clsx from 'clsx';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { authUserSelector } from '../../auth';
import isEditable from '../../orders/services/isEditable';
import {
  PriceBreakdownModel,
  QuickSaleAddressModel,
  QuickSalePlanModel,
} from '../models/QuickSaleModel';
import { simulateDraft } from '../services/QuickSaleCRUD';
import {
  apiErrorMessage,
  formatEuro,
  orderLinesSummary,
} from '../utils/quickSaleFormat';
import {
  ALL_FILTER,
  regularNumber,
  UPCOMING_FILTER,
  visibleOrders,
} from '../utils/quickSaleFilters';
import { OrderEditModal } from './OrderEditModal';
import { PriceBreakdown } from './PriceBreakdown';
import { QuickSaleCard } from './QuickSaleCard';
import { StateBadge } from './StateBadge';

type Props = {
  userId: number;
  petId: number;
  storeId: number;
  plan: QuickSalePlanModel;
  addresses: QuickSaleAddressModel[];
  isSimulation: boolean;
  filter: string;
  onFilterChange: (filter: string) => void;
  onChanged: () => Promise<void> | void;
};

const OrdersCard: FC<Props> = ({
  userId,
  petId,
  storeId,
  plan,
  addresses,
  isSimulation,
  filter,
  onFilterChange,
  onChanged,
}) => {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector, shallowEqual);
  const [editing, setEditing] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
  const [customPrice, setCustomPrice] = useState<PriceBreakdownModel | null>(
    null
  );
  const [pricingCustom, setPricingCustom] = useState(false);
  const orders = visibleOrders(plan, filter);
  const states = Array.from(
    new Set(
      [...plan.orders.pending, ...plan.orders.history].map(
        (order) => order.state
      )
    )
  );
  const editingOrder = plan.orders.pending.find(
    (order) => order.id && order.id === editingOrderId
  );
  const customOption = plan.custom_option;

  async function priceSavedCustom() {
    if (!customOption) {
      return;
    }
    setPricingCustom(true);
    try {
      const { data } = await simulateDraft(userId, {
        pet_id: petId,
        plan_id: plan.id,
        draft: {
          plan: {
            kind: 'custom',
            custom_lines: customOption.lines.map(
              ({ meal, meal_size, units }) => ({ meal, meal_size, units })
            ),
          },
        },
      });
      setCustomPrice(data.plan?.price || null);
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.genericError' })
        )
      );
    } finally {
      setPricingCustom(false);
    }
  }

  return (
    <QuickSaleCard
      icon="fa-shopping-cart"
      title={
        <>
          {intl.formatMessage({ id: 'quickSale.orders' })}
          {isSimulation && (
            <span className="badge badge-light-primary ms-3">
              {intl.formatMessage({ id: 'quickSale.simulation' })}
            </span>
          )}
        </>
      }
      editing={editing}
      onToggleEdit={() => setEditing(!editing)}
      actions={
        <select
          className="form-select form-select-sm w-auto"
          value={filter}
          aria-label={intl.formatMessage({ id: 'quickSale.filterByState' })}
          onChange={(event) => onFilterChange(event.target.value)}
        >
          <option value={UPCOMING_FILTER}>
            {intl.formatMessage({ id: 'quickSale.upcomingOrders' })}
          </option>
          <option value={ALL_FILTER}>
            {intl.formatMessage({ id: 'quickSale.allStates' })}
          </option>
          {states.map((state) => (
            <option key={state} value={state}>
              {intl.formatMessage({ id: `quickSaleOrderState.${state}` })}
            </option>
          ))}
        </select>
      }
    >
      <div className="table-responsive">
        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
          <thead>
            <tr className="fw-bolder text-muted bg-light">
              <th className="ps-4 rounded-start w-40px">#</th>
              <th className="min-w-125px">
                {intl.formatMessage({ id: 'quickSale.type' })}
              </th>
              <th>{intl.formatMessage({ id: 'quickSale.recipesSummary' })}</th>
              <th>{intl.formatMessage({ id: 'quickSale.price' })}</th>
              <th>{intl.formatMessage({ id: 'quickSale.shipping' })}</th>
              <th className={clsx(!editing && 'rounded-end')}>
                {intl.formatMessage({ id: 'quickSale.state' })}
              </th>
              {editing && <th className="rounded-end" />}
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => {
              const number = regularNumber(plan, order);
              const coupons = (order.coupons || [])
                .map((coupon) => coupon?.discount?.code)
                .filter(Boolean)
                .join(', ');
              return (
                <tr
                  key={order.id ?? `projected-${index}`}
                  className={clsx(order.is_projected && 'opacity-75')}
                >
                  <td className="ps-4 text-gray-600">{index + 1}</td>
                  <td>
                    {order.is_trial ? (
                      <span className="badge badge-info">
                        {intl.formatMessage({ id: 'quickSale.trial' })}
                      </span>
                    ) : (
                      <span className="badge badge-light-primary">
                        {number
                          ? intl.formatMessage(
                              { id: 'quickSale.orderNumber' },
                              { number }
                            )
                          : intl.formatMessage({
                              id: 'quickSale.regularOrder',
                            })}
                      </span>
                    )}
                    {order.combine && (
                      <i
                        className="fas fa-adjust text-primary ms-2"
                        title={intl.formatMessage({
                          id: 'quickSale.complementary',
                        })}
                      />
                    )}
                    <div className="fs-8 text-muted mt-1">
                      {order.promise_date
                        ? new Date(order.promise_date).toLocaleDateString()
                        : '-'}
                      {order.id && (
                        <Link
                          to={`/user/${userId}/order/${order.id}`}
                          className="link ms-2"
                          title={intl.formatMessage({
                            id: 'quickSale.otherDetails',
                          })}
                        >
                          {order.code}
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="text-gray-800">
                    {orderLinesSummary(order.lines).join(' + ') || '-'}
                  </td>
                  <td title={coupons || undefined}>
                    <PriceBreakdown price={order.price} />
                    {coupons && (
                      <i className="fas fa-ticket-alt text-success ms-2" />
                    )}
                  </td>
                  <td>{formatEuro(intl, order.price?.shipping)}</td>
                  <td>
                    <StateBadge state={order.state} kind="order" />
                  </td>
                  {editing && (
                    <td className="text-end">
                      {order.id &&
                        !isSimulation &&
                        plan.orders.pending.includes(order) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-light"
                            onClick={() => setEditingOrderId(order.id)}
                          >
                            <i className="fas fa-pen fs-7 me-1" />
                            {intl.formatMessage({ id: 'quickSale.edit' })}
                          </button>
                        )}
                    </td>
                  )}
                </tr>
              );
            })}
            {!orders.length && (
              <tr>
                <td
                  colSpan={editing ? 7 : 6}
                  className="text-center text-muted py-5"
                >
                  {intl.formatMessage({ id: 'quickSale.noOrders' })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {customOption && (
        <div className="bg-light rounded p-4 mt-4 d-flex flex-wrap align-items-center gap-4">
          <span className="fw-bolder">
            {intl.formatMessage({
              id: customOption.is_active
                ? 'quickSale.customActive'
                : 'quickSale.customSaved',
            })}
          </span>
          <span className="text-gray-700">
            {customOption.lines
              .map(
                (line) =>
                  `${line.units}x ${line.meal_name} (${line.weight_in_gr} g)`
              )
              .join(' + ')}
          </span>
          {customOption.grams_per_day && (
            <span className="text-gray-700">
              {customOption.grams_per_day} g / día
            </span>
          )}
          {customOption.is_active ? (
            <PriceBreakdown price={plan.price} />
          ) : customPrice ? (
            <PriceBreakdown price={customPrice} />
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-light-primary"
              disabled={pricingCustom}
              onClick={priceSavedCustom}
            >
              {pricingCustom && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              {intl.formatMessage({ id: 'quickSale.priceSavedCustom' })}
            </button>
          )}
        </div>
      )}

      <div className="pt-4">
        <Link to={`/user/${userId}/orders`} className="link fw-bold">
          {intl.formatMessage({ id: 'quickSale.allOrders' })}
        </Link>
      </div>

      {editingOrder && (
        <OrderEditModal
          order={editingOrder}
          storeId={storeId}
          addresses={addresses}
          linesEditable={isEditable({ order: editingOrder, authUser })}
          onClose={() => setEditingOrderId(null)}
          onChanged={onChanged}
        />
      )}
    </QuickSaleCard>
  );
};

export { OrdersCard };
